import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ELASTIC_SEARCH_CONSTANTS } from '@constants/elastic-search/elastic-search-constants.constant';
import { ELASTIC_SEARCH_FIELDS } from '@constants/elastic-search/elastic-search-fields.constant';
import { ELASTIC_SEARCH_RETURNED_FIELDS } from '@constants/elastic-search/elastic-search-returned-fields.constant';
import { ELASTIC_SEARCH_TYPE } from '@constants/elastic-search/elastic-search-type.enum';
import { GATEWAY_TYPE } from '@constants/gateway/gateway-type';
import { HTTPS_REQUEST_OPTIONS } from '@constants/https-request-options/https-request-options';
import { ElasticSearchFilterValue } from '@models/elastic-search/request/elastic-search-filter-value';
import { ElasticSearchRequest } from '@models/elastic-search/request/elastic-search-request';
import { ELASTIC_SEARCH_SORT_ORDER } from '@models/elastic-search/request/elastic-search-sort-order.enum';
import { ElasticSearchSortValue } from '@models/elastic-search/request/elastic-search-sort-value';
import { ElasticSearchResponseEntity } from '@models/elastic-search/response/elastic-search-response-entity';
import { Role } from '@models/user/role';
import { User } from '@models/user/user';
import { ApiLocationService } from '@services/api-location/api-location.service';
import { map, Observable, of, Subscriber } from 'rxjs';

export function generateElasticSearchText(searchedValue: string): string {
  if (!searchedValue || searchedValue.trim() === '') {
    return '*';
  }

  const regexPattern = new RegExp(ELASTIC_SEARCH_CONSTANTS.SPECIAL_CHARACTERS_PATTERN.join('|'), 'g');
  const keywords = searchedValue.trim().split(regexPattern);
  const isQuotedValue = searchedValue.startsWith('"') && searchedValue.endsWith('"');

  if (isQuotedValue && keywords.length > 1) {
    const sanitizedKeywords = keywords.map((keyword: string) => keyword.trim()).filter(Boolean);
    return sanitizedKeywords.join(` ${ELASTIC_SEARCH_CONSTANTS.SEARCH_ITEMS_JOIN} `);
  }

  if (keywords.length > 1) {
    const sanitizedKeywords = keywords.map((keyword: string) => keyword.trim() !== '' ? `*${keyword.trim()}*` : '');
    return sanitizedKeywords.filter(Boolean).join(` ${ELASTIC_SEARCH_CONSTANTS.SEARCH_ITEMS_JOIN} `);
  }

  return `*${keywords[0].trim()}*`;
}

@Injectable({
  providedIn: 'root'
})
export class ElasticSearchService {

  private ELASTIC_API_PREFIX = '/api/elastic/';
  private ITEMS_COUNT_SUFFIX = '/count';

  private readonly BASE_URL: string;
  private readonly MAX_ITEMS_PER_PAGE = 9999;
  private readonly INITIAL_PAGE_NUMBER = 0;

  private readonly NAME_FIELD: string = 'name';
  private readonly ID_FIELD: string = 'id';
  private readonly FIREBASE_USER_ID_FIELD: string = 'firebaseUid';
  private readonly USER_NAME_FIELD: string = 'username';
  private readonly CATEGORY_FIELD: string = 'category';
  private readonly MACHINERY_GROUP_FIELD: string = 'machineGroup';
  private readonly PARENT_ID_FIELD: string = 'parentId';
  private readonly GATEWAY_TYPE_FIELD: string = 'cloudplugType';

  constructor(
      private http: HttpClient,
      private apiLocation: ApiLocationService) {
    this.BASE_URL = this.apiLocation.getLocation();
  }

  public listAllOrganizations(organizationId: number | null): Observable<ElasticSearchResponseEntity> {
    const searchText: string = '';
    const sortValue: ElasticSearchSortValue = new ElasticSearchSortValue(this.NAME_FIELD, ELASTIC_SEARCH_SORT_ORDER.ASC);
    const pageIndex: number = this.INITIAL_PAGE_NUMBER;
    const limit: number = this.MAX_ITEMS_PER_PAGE;
    const type: ELASTIC_SEARCH_TYPE = ELASTIC_SEARCH_TYPE.ORGANIZATION;
    const fieldsToSearch: string[] = ELASTIC_SEARCH_FIELDS.ORGANIZATION_SEARCH_FIELDS;
    const fieldsToReturn: string[] = ELASTIC_SEARCH_RETURNED_FIELDS.ORGANIZATION_RETURNED_FIELDS;
    return this.list(type, searchText, sortValue, fieldsToSearch, fieldsToReturn, pageIndex, limit, organizationId, null, null);
  }


  public listOrganizations(searchText: string, sortValue: ElasticSearchSortValue, pageNumber: number, limit: number, organizationId: number | null): Observable<any> {
    const type: ELASTIC_SEARCH_TYPE = ELASTIC_SEARCH_TYPE.ORGANIZATION;
    const fieldsToSearch: string[] = ELASTIC_SEARCH_FIELDS.ORGANIZATION_SEARCH_FIELDS;
    const fieldsToReturn: string[] = ELASTIC_SEARCH_RETURNED_FIELDS.ORGANIZATION_RETURNED_FIELDS;
    return this.list(type, searchText, sortValue, fieldsToSearch, fieldsToReturn, pageNumber - 1, limit, organizationId, null, null);
  }

  public countOrganizations(organizationId: number | null): Observable<any> {
    const type: ELASTIC_SEARCH_TYPE = ELASTIC_SEARCH_TYPE.ORGANIZATION;
    return this.count(type, organizationId, null, null);
  }

  public listAllMachineryGroups(machineryGroupId: number | null, whiteListedMachineryGroups: number[] | null, rootMachineryGroup: number | null = null): Observable<any> {
    const searchText: string = '';
    const sortValue: ElasticSearchSortValue = new ElasticSearchSortValue(this.NAME_FIELD, ELASTIC_SEARCH_SORT_ORDER.ASC);
    const pageIndex: number = this.INITIAL_PAGE_NUMBER;
    const limit: number = this.MAX_ITEMS_PER_PAGE;
    const type: ELASTIC_SEARCH_TYPE = ELASTIC_SEARCH_TYPE.MACHINERY_GROUP;
    const fieldsToSearch: string[] = ELASTIC_SEARCH_FIELDS.MACHINERY_GROUP_SEARCH_FIELDS;
    const fieldsToReturn: string[] = ELASTIC_SEARCH_RETURNED_FIELDS.MACHINERY_GROUP_RETURNED_FIELDS;
    const usableMachineryGroupIds: number[] | null = !machineryGroupId || machineryGroupId <= 0 ? null : [machineryGroupId];
    return this.list(type, searchText, sortValue, fieldsToSearch, fieldsToReturn, pageIndex, limit, null, usableMachineryGroupIds, whiteListedMachineryGroups, false, null, rootMachineryGroup);
  }

  public listMachineryGroups(searchText: string, sortValue: ElasticSearchSortValue, pageNumber: number, limit: number, machineryGroupId: number | null, whiteListedMachineryGroups: number[] | null): Observable<any> {
    const type: ELASTIC_SEARCH_TYPE = ELASTIC_SEARCH_TYPE.MACHINERY_GROUP;
    const fieldsToSearch: string[] = ELASTIC_SEARCH_FIELDS.MACHINERY_GROUP_SEARCH_FIELDS;
    const fieldsToReturn: string[] = ELASTIC_SEARCH_RETURNED_FIELDS.MACHINERY_GROUP_RETURNED_FIELDS;
    const usableMachineryGroupIds: number[] | null = !machineryGroupId || machineryGroupId <= 0 ? null : [machineryGroupId];
    return this.list(type, searchText, sortValue, fieldsToSearch, fieldsToReturn, pageNumber - 1, limit, null, usableMachineryGroupIds, whiteListedMachineryGroups);
  }

  public countMachineryGroups(machineryGroupId: number | null, whiteListedMachineryGroups: number[] | null): Observable<any> {
    const type: ELASTIC_SEARCH_TYPE = ELASTIC_SEARCH_TYPE.MACHINERY_GROUP;
    const usableMachineryGroupIds: number[] | null = !machineryGroupId || machineryGroupId <= 0 ? null : [machineryGroupId];
    return this.count(type, null, usableMachineryGroupIds, whiteListedMachineryGroups);
  }

  public listAllMachines(organizationId: number | null, machineryGroupId: number | null, whiteListedMachineryGroups: number[] | null): Observable<any> {
    const searchText: string = '';
    const sortValue: ElasticSearchSortValue = new ElasticSearchSortValue(this.NAME_FIELD, ELASTIC_SEARCH_SORT_ORDER.ASC);
    const pageIndex: number = this.INITIAL_PAGE_NUMBER;
    const limit: number = this.MAX_ITEMS_PER_PAGE;
    const onlyTemplates: boolean = false;
    const type: ELASTIC_SEARCH_TYPE = ELASTIC_SEARCH_TYPE.MACHINE;
    const fieldsToSearch: string[] = ELASTIC_SEARCH_FIELDS.MACHINE_SEARCH_FIELDS;
    const fieldsToReturn: string[] = ELASTIC_SEARCH_RETURNED_FIELDS.MACHINE_RETURNED_FIELDS;
    const usableMachineryGroupIds: number[] | null = !machineryGroupId || machineryGroupId <= 0 ? null : [machineryGroupId];
    return this.list(type, searchText, sortValue, fieldsToSearch, fieldsToReturn, pageIndex, limit, organizationId, usableMachineryGroupIds, whiteListedMachineryGroups, onlyTemplates);
  }

  public listMachines(searchText: string, sortValue: ElasticSearchSortValue, pageNumber: number, limit: number, organizationId: number | null, machineryGroupId: number | null, whiteListedMachineryGroups: number[] | null, onlyTemplates: boolean = false): Observable<any> {
    const type: ELASTIC_SEARCH_TYPE = ELASTIC_SEARCH_TYPE.MACHINE;
    const fieldsToSearch: string[] = ELASTIC_SEARCH_FIELDS.MACHINE_SEARCH_FIELDS;
    const fieldsToReturn: string[] = ELASTIC_SEARCH_RETURNED_FIELDS.MACHINE_RETURNED_FIELDS;
    const usableMachineryGroupIds: number[] | null = !machineryGroupId || machineryGroupId <= 0 ? null : [machineryGroupId];
    return this.list(type, searchText, sortValue, fieldsToSearch, fieldsToReturn, pageNumber - 1, limit, organizationId, usableMachineryGroupIds, whiteListedMachineryGroups, onlyTemplates);
  }

  public countMachines(organizationId: number | null, machineryGroupId: number | null, whiteListedMachineryGroups: number[] | null): Observable<any> {
    const type: ELASTIC_SEARCH_TYPE = ELASTIC_SEARCH_TYPE.MACHINE;
    const usableMachineryGroupIds: number[] | null = !machineryGroupId || machineryGroupId <= 0 ? null : [machineryGroupId];
    return this.count(type, organizationId, usableMachineryGroupIds, whiteListedMachineryGroups);
  }

  public listAllGateways(organizationId: number | null, machineryGroupId: number | null, whiteListedMachineryGroups: number[] | null, isSuperAdmin: boolean): Observable<any> {
    const searchText: string = '';
    const sortValue: ElasticSearchSortValue = new ElasticSearchSortValue(this.NAME_FIELD, ELASTIC_SEARCH_SORT_ORDER.ASC);
    const pageNumber: number = this.INITIAL_PAGE_NUMBER;
    const limit: number = this.MAX_ITEMS_PER_PAGE;

    return this.listGateways(searchText, sortValue, pageNumber, limit, organizationId, machineryGroupId, whiteListedMachineryGroups, isSuperAdmin);
  }

  public isGatewayPropertyAlreadyUsed(propertyValue: string, isSerialNumber: boolean): Observable<any> {
    let fieldName: string = 'id';
    if (!isSerialNumber) {
      fieldName = 'name';
    }
    const type: ELASTIC_SEARCH_TYPE = ELASTIC_SEARCH_TYPE.GATEWAY;

    const countRequest: ElasticSearchRequest = new ElasticSearchRequest();
    const serialNumberFilter: ElasticSearchFilterValue = new ElasticSearchFilterValue(fieldName, propertyValue.trim(), true);
    countRequest.filter = [serialNumberFilter];

    const url: string = this.BASE_URL + this.ELASTIC_API_PREFIX + type + this.ITEMS_COUNT_SUFFIX;
    return this.http.post(url, countRequest, HTTPS_REQUEST_OPTIONS);
  }

  public listGateways(searchText: string, sortValue: ElasticSearchSortValue, pageNumber: number, limit: number, organizationId: number | null, machineryGroupId: number | null, whiteListedMachineryGroups: number[] | null, isSuperAdmin: boolean): Observable<any> {

    const type: ELASTIC_SEARCH_TYPE = ELASTIC_SEARCH_TYPE.GATEWAY;
    const fieldsToSearch: string[] = ELASTIC_SEARCH_FIELDS.GATEWAY_SEARCH_FIELDS;
    const fieldsToReturn: string[] = ELASTIC_SEARCH_RETURNED_FIELDS.GATEWAY_RETURNED_FIELDS;

    const fallbackResponse: ElasticSearchResponseEntity = new ElasticSearchResponseEntity();
    if (isSuperAdmin) {
      const usableMachineryGroupIds: number[] | null = !machineryGroupId || machineryGroupId <= 0 ? null : [machineryGroupId];
      return this.list(type, searchText, sortValue, fieldsToSearch, fieldsToReturn, pageNumber - 1, limit, organizationId, usableMachineryGroupIds, whiteListedMachineryGroups);
    }

    return new Observable<ElasticSearchResponseEntity>((observer: Subscriber<any>) => {
      this.listAllMachines(organizationId, machineryGroupId, whiteListedMachineryGroups).subscribe(
          {
            next: (machinesResponse: ElasticSearchResponseEntity) => {
              const type: ELASTIC_SEARCH_TYPE = ELASTIC_SEARCH_TYPE.GATEWAY;
              const fieldsToSearch: string[] = ELASTIC_SEARCH_FIELDS.GATEWAY_SEARCH_FIELDS;
              const fieldsToReturn: string[] = ELASTIC_SEARCH_RETURNED_FIELDS.GATEWAY_RETURNED_FIELDS;
              if (!machinesResponse?.result?.data) {
                observer.next(fallbackResponse);
              }
              const map = machinesResponse.result.data.map(m => {
                return !m?.id ? -1 : m.id;
              });
              const usableMachineIds: number[] | null = map && map.length > 0 ? map : [1];
              this.list(type, searchText, sortValue, fieldsToSearch, fieldsToReturn, pageNumber - 1, limit, organizationId, usableMachineIds, whiteListedMachineryGroups)
              .subscribe({
                next: (success: ElasticSearchResponseEntity) => {
                  observer.next(success);
                }, error: (_error) => {
                  observer.next(fallbackResponse);
                }
              });
            }, error: (_error) => {
              observer.next(fallbackResponse);
            }
          });
    });
  }

  public listAllGatewaysByType(organizationId: number | null, machineryGroupId: number | null, whiteListedMachineryGroups: number[] | null, isSuperAdmin: boolean, gatewayType: GATEWAY_TYPE): Observable<any> {
    const fallbackResponse: ElasticSearchResponseEntity = new ElasticSearchResponseEntity();
    const type: ELASTIC_SEARCH_TYPE = ELASTIC_SEARCH_TYPE.GATEWAY;
    const request = new ElasticSearchRequest;
    request.fieldsToSearch = ELASTIC_SEARCH_FIELDS.GATEWAY_SEARCH_FIELDS;

    request.fieldsToReturn = ELASTIC_SEARCH_RETURNED_FIELDS.GATEWAY_RETURNED_FIELDS;
    request.from = 0;
    request.size = this.MAX_ITEMS_PER_PAGE;
    request.sort = new ElasticSearchSortValue(this.NAME_FIELD, ELASTIC_SEARCH_SORT_ORDER.ASC, false);
    const filters: ElasticSearchFilterValue[] = [];
    const url: string = this.BASE_URL + this.ELASTIC_API_PREFIX + type + '/';
    if (isSuperAdmin) {
      filters.push(new ElasticSearchFilterValue(this.GATEWAY_TYPE_FIELD, gatewayType, true));
      request.filter = filters;
      return this.http.post(url, request, HTTPS_REQUEST_OPTIONS);
    }

    return new Observable<ElasticSearchResponseEntity>((observer: Subscriber<any>) => {
      this.listAllMachines(organizationId, machineryGroupId, whiteListedMachineryGroups).subscribe(
          {
            next: (machinesResponse: ElasticSearchResponseEntity) => {

              if (!machinesResponse?.result?.data) {
                observer.next(fallbackResponse);
              }
              const map = machinesResponse.result.data.map(m => {
                return !m?.id ? -1 : m.id;
              });
              const usableMachineryGroupIds: number[] | null = map && map.length > 0 ? map : [1];

              const whiteListFilters: ElasticSearchFilterValue[] = [];
              if (usableMachineryGroupIds && usableMachineryGroupIds.length > 0) {
                filters.push(new ElasticSearchFilterValue(ELASTIC_SEARCH_CONSTANTS.MACHINERY_GROUP_ANCESTORS_PROPERTY_NAME, usableMachineryGroupIds.join(',')));
              }
              if (organizationId && organizationId > 0) {
                filters.push(new ElasticSearchFilterValue(ELASTIC_SEARCH_CONSTANTS.ORGANIZATION_ANCESTORS_PROPERTY_NAME, String(organizationId)));
              }

              if (whiteListedMachineryGroups && whiteListedMachineryGroups.length > 0) {
                whiteListFilters.push(new ElasticSearchFilterValue(this.PARENT_ID_FIELD, String(whiteListedMachineryGroups.join(','))));
              }

              filters.push(new ElasticSearchFilterValue(this.GATEWAY_TYPE_FIELD, gatewayType, true));
              request.filter = filters;
              request.whiteListFilter = whiteListFilters;

              const url: string = this.BASE_URL + this.ELASTIC_API_PREFIX + type + '/';
              this.http.post(url, request, HTTPS_REQUEST_OPTIONS).subscribe({
                next: (success: any) => {
                  observer.next(success);
                },
                error: (_error) => {
                  observer.next(fallbackResponse);
                }
              });
            }, error: (_error) => {
              observer.next(fallbackResponse);
            }
          });
    });

  }

  public getGenericGatewaysByMachineId(machineId: number): Observable<any> {
    const fallbackResponse: ElasticSearchResponseEntity = new ElasticSearchResponseEntity();
    const type: ELASTIC_SEARCH_TYPE = ELASTIC_SEARCH_TYPE.GATEWAY;
    const request = new ElasticSearchRequest;
    request.fieldsToSearch = [this.PARENT_ID_FIELD, this.GATEWAY_TYPE_FIELD];

    request.fieldsToReturn = ELASTIC_SEARCH_RETURNED_FIELDS.GATEWAY_RETURNED_FIELDS;
    request.from = 0;
    request.size = this.MAX_ITEMS_PER_PAGE;
    request.sort = new ElasticSearchSortValue(this.NAME_FIELD, ELASTIC_SEARCH_SORT_ORDER.ASC, false);
    const filters: ElasticSearchFilterValue[] = [];
    filters.push(new ElasticSearchFilterValue(this.PARENT_ID_FIELD, machineId.toString()));
    filters.push(new ElasticSearchFilterValue(this.GATEWAY_TYPE_FIELD, GATEWAY_TYPE.GENERIC, true));
    const url: string = this.BASE_URL + this.ELASTIC_API_PREFIX + type + '/';
    request.filter = filters;
    return this.http.post(url, request, HTTPS_REQUEST_OPTIONS);
  }

  public countGateways(organizationId: number | null, machineryGroupId: number | null, whiteListedMachineryGroups: number[] | null, isSuperAdmin: boolean) {
    const type: ELASTIC_SEARCH_TYPE = ELASTIC_SEARCH_TYPE.GATEWAY;
    if (isSuperAdmin) {
      const usableMachineryGroupIds: number[] | null = !machineryGroupId || machineryGroupId <= 0 ? null : [machineryGroupId];
      return this.count(type, organizationId, usableMachineryGroupIds, null);
    }


    const fallbackResponse: ElasticSearchResponseEntity = new ElasticSearchResponseEntity();
    return new Observable<ElasticSearchResponseEntity>((observer: Subscriber<ElasticSearchResponseEntity>) => {
      this.listAllMachines(organizationId, machineryGroupId, whiteListedMachineryGroups).subscribe(
          {
            next: (machinesResponse: ElasticSearchResponseEntity) => {
              const type: ELASTIC_SEARCH_TYPE = ELASTIC_SEARCH_TYPE.GATEWAY;
              if (!machinesResponse?.result?.data) {
                observer.next(fallbackResponse);
              }
              const map = machinesResponse.result.data.map(m => {
                return !m?.id ? -1 : m.id;
              });
              const usableMachineIds: number[] | null = map && map.length > 0 ? map : [1];
              this.count(type, organizationId, usableMachineIds, whiteListedMachineryGroups).subscribe({
                next: (success: ElasticSearchResponseEntity) => {
                  observer.next(success);
                }, error: (_error) => {
                  observer.next(fallbackResponse);
                }
              });


            }, error: (_error) => {
              observer.next(fallbackResponse);
            }
          });
    });
  }

  public listAllUsers(organizationId: number | null, machineryGroupId: number | null, whiteListedMachineryGroups: number[] | null, loggedUserId: string): Observable<any> {
    const searchText: string = '';
    const sortValue: ElasticSearchSortValue = new ElasticSearchSortValue(this.USER_NAME_FIELD, ELASTIC_SEARCH_SORT_ORDER.ASC);
    const pageIndex: number = this.INITIAL_PAGE_NUMBER;
    const limit: number = this.MAX_ITEMS_PER_PAGE;
    const type: ELASTIC_SEARCH_TYPE = ELASTIC_SEARCH_TYPE.USER;
    const fieldsToSearch: string[] = ELASTIC_SEARCH_FIELDS.USER_SEARCH_FIELDS;
    const fieldsToReturn: string[] = ELASTIC_SEARCH_RETURNED_FIELDS.USER_RETURNED_FIELDS;
    const usableMachineryGroupIds: number[] | null = !machineryGroupId || machineryGroupId <= 0 ? null : [machineryGroupId];
    return this.list(type, searchText, sortValue, fieldsToSearch, fieldsToReturn, pageIndex, limit, organizationId, usableMachineryGroupIds, whiteListedMachineryGroups, false, loggedUserId);

  }

  listAllAccessibleUsers(user: User): Observable<User[]> {
    let parentId = user.parentId;
    let machineGroup: number | null = user.machineGroup;
    let whiteListedMachineryGroups: number[] | null = user.getWhiteListedMachineryGroups();
    if (new Role(user).isSuperAdminRole) {
      parentId = -1;
      machineGroup = null;
      whiteListedMachineryGroups = null;
    }
    return this.listAllUsers(parentId, machineGroup, whiteListedMachineryGroups, user.firebaseUid).pipe(map(users => users.result.data));
  }

  public listUsers(searchText: string, sortValue: ElasticSearchSortValue, pageNumber: number, limit: number, organizationId: number | null, machineryGroupId: number | null, whiteListedMachineryGroups: number[] | null, loggedUserId: string): Observable<any> {
    const type: ELASTIC_SEARCH_TYPE = ELASTIC_SEARCH_TYPE.USER;
    const fieldsToSearch: string[] = ELASTIC_SEARCH_FIELDS.USER_SEARCH_FIELDS;
    const fieldsToReturn: string[] = ELASTIC_SEARCH_RETURNED_FIELDS.USER_RETURNED_FIELDS;
    const usableMachineryGroupIds: number[] | null = !machineryGroupId || machineryGroupId <= 0 ? null : [machineryGroupId];
    return this.list(type, searchText, sortValue, fieldsToSearch, fieldsToReturn, pageNumber - 1, limit, organizationId, usableMachineryGroupIds, whiteListedMachineryGroups, false, loggedUserId);
  }

  public countUsers(organizationId: number | null,
                    machineryGroupId: number | null,
                    whiteListedMachineryGroups: number[] | null,
                    loggedUserId: string) {
    const type: ELASTIC_SEARCH_TYPE = ELASTIC_SEARCH_TYPE.USER;
    const usableMachineryGroupIds: number[] | null = !machineryGroupId || machineryGroupId <= 0 ? null : [machineryGroupId];

    return this.count(type, organizationId, usableMachineryGroupIds, whiteListedMachineryGroups, loggedUserId);
  }


  public listMachinesByCategory(category: string): Observable<any> {
    const type: ELASTIC_SEARCH_TYPE = ELASTIC_SEARCH_TYPE.MACHINE;
    const sortValue: ElasticSearchSortValue = new ElasticSearchSortValue(this.NAME_FIELD, ELASTIC_SEARCH_SORT_ORDER.ASC, false);
    const fieldsToSearch: string[] = [this.CATEGORY_FIELD];
    const fieldsToReturn: string[] = ELASTIC_SEARCH_RETURNED_FIELDS.MACHINE_RETURNED_FIELDS;
    const request = new ElasticSearchRequest;
    request.from = this.INITIAL_PAGE_NUMBER;
    request.size = this.MAX_ITEMS_PER_PAGE;
    request.fieldsToSearch = fieldsToSearch;
    request.fieldsToReturn = fieldsToReturn;
    request.searchText = this.generateSearchText(category);

    if (sortValue.order !== ELASTIC_SEARCH_SORT_ORDER.NONE) {
      request.sort = sortValue;
    }

    const url: string = this.BASE_URL + this.ELASTIC_API_PREFIX + type + '/';
    return this.http.post(url, request, HTTPS_REQUEST_OPTIONS);
  }


  public getBaseMenuItemById(menuItemId: string | null | undefined, rootMachineryGroupId: number | null, whiteListedMachineryGroups: number[] | null, isSuperAdmin: boolean): Observable<any> {

    if (!menuItemId || Number(menuItemId) === rootMachineryGroupId) {
      return of(null);
    }

    const type: ELASTIC_SEARCH_TYPE = ELASTIC_SEARCH_TYPE.BASE_MENU_ITEM;
    const fieldsToSearch: string[] = ELASTIC_SEARCH_FIELDS.BASE_MENU_SEARCH_FIELDS;
    const fieldsToReturn: string[] = ELASTIC_SEARCH_RETURNED_FIELDS.BASE_MENU_RETURNED_FIELDS;
    const request: ElasticSearchRequest = new ElasticSearchRequest();
    request.fieldsToSearch = fieldsToSearch;
    request.fieldsToReturn = fieldsToReturn;
    request.searchText = '*';

    const groupFilterValue: ElasticSearchFilterValue = new ElasticSearchFilterValue('id', String(menuItemId), false);
    request.filter = [groupFilterValue];
    let whiteListFilters: ElasticSearchFilterValue[] = [];

    if (!isSuperAdmin && whiteListedMachineryGroups && whiteListedMachineryGroups.length > 0) {
      whiteListFilters = this.addFilterIfDefined(whiteListFilters, new ElasticSearchFilterValue(this.ID_FIELD, String(whiteListedMachineryGroups.join(','))));
      if (rootMachineryGroupId) {
        whiteListFilters = this.addFilterIfDefined(whiteListFilters, new ElasticSearchFilterValue(this.ID_FIELD, String(rootMachineryGroupId)));
      }
      request.whiteListFilter = whiteListFilters;
    }


    const url: string = this.BASE_URL + this.ELASTIC_API_PREFIX + type + '/';
    return this.http.post(url, request, HTTPS_REQUEST_OPTIONS);
  }

  private list(type: ELASTIC_SEARCH_TYPE,
               searchText: string,
               sortValue: ElasticSearchSortValue,
               fieldsToSearch: string[],
               fieldsToReturn: string[],
               pageIndex: number,
               limit: number,
               organizationId: number | null,
               machineryGroupOrMachinesIdsList: number[] | null,
               whiteListedMachineryGroups: number[] | null,
               onlyTemplates: boolean = false,
               currentLoggedUserId: number | string | null = null,
               rootMachineryGroup: number | null = null): Observable<any> {
    const request = new ElasticSearchRequest;
    request.fieldsToSearch = fieldsToSearch;
    request.fieldsToReturn = fieldsToReturn;
    request.searchText = this.generateSearchText(searchText);
    request.from = pageIndex * limit;
    request.size = limit;
    if (sortValue.order !== ELASTIC_SEARCH_SORT_ORDER.NONE) {
      request.sort = sortValue;
    }

    this.addFiltersToRequest(request, type, organizationId, machineryGroupOrMachinesIdsList, whiteListedMachineryGroups, currentLoggedUserId, rootMachineryGroup);

    if (onlyTemplates) {
      request.filter.push(new ElasticSearchFilterValue(ELASTIC_SEARCH_CONSTANTS.TEMPLATE_MACHINE_PROPERTY_NAME, String(onlyTemplates)));
    }
    const url: string = this.BASE_URL + this.ELASTIC_API_PREFIX + type + '/';
    return this.http.post(url, request, HTTPS_REQUEST_OPTIONS);
  }

  private count(type: ELASTIC_SEARCH_TYPE, organizationId: number | null, machineryGroupOrMachinesIdsList: number[] | null, whiteListedMachineryGroups: number[] | null, currentLoggedUserId: number | string | null = null): Observable<any> {
    const countRequest: ElasticSearchRequest = new ElasticSearchRequest();
    this.addFiltersToRequest(countRequest, type, organizationId, machineryGroupOrMachinesIdsList, whiteListedMachineryGroups, currentLoggedUserId);
    const url: string = this.BASE_URL + this.ELASTIC_API_PREFIX + type + this.ITEMS_COUNT_SUFFIX;
    return this.http.post(url, countRequest, HTTPS_REQUEST_OPTIONS);
  }

  private addFilterIfDefined(filters: ElasticSearchFilterValue[], filter: ElasticSearchFilterValue | null): ElasticSearchFilterValue[] {
    if (!filter) {
      return filters;
    }
    return [...filters, filter];
  }


  private generateSearchText(searchedValue: string): string {
    return generateElasticSearchText(searchedValue);
  }


  private addFiltersToRequest(request: ElasticSearchRequest, type: ELASTIC_SEARCH_TYPE, organizationId: number | null, machineryGroupOrMachinesIdsList: number[] | null, whiteListedMachineryGroups: number[] | null, currentLoggedUserId: number | string | null, rootMachineryGroup: number | null = null): void {
    let groupFilterValue: ElasticSearchFilterValue | null = null;
    let organizationFilterValue: ElasticSearchFilterValue | null = null;

    if (machineryGroupOrMachinesIdsList && machineryGroupOrMachinesIdsList.length > 0) {
      groupFilterValue = new ElasticSearchFilterValue(ELASTIC_SEARCH_CONSTANTS.MACHINERY_GROUP_ANCESTORS_PROPERTY_NAME, machineryGroupOrMachinesIdsList.join(','));
    }

    if (organizationId && organizationId > 0) {
      organizationFilterValue = new ElasticSearchFilterValue(ELASTIC_SEARCH_CONSTANTS.ORGANIZATION_ANCESTORS_PROPERTY_NAME, String(organizationId));
    }


    let filters: ElasticSearchFilterValue[] = [];
    let whiteListFilters: ElasticSearchFilterValue[] = [];

    switch (type) {
      case ELASTIC_SEARCH_TYPE.ORGANIZATION:
        filters = this.addFilterIfDefined(filters, organizationFilterValue);
        break;
      case ELASTIC_SEARCH_TYPE.MACHINERY_GROUP:
        if (whiteListedMachineryGroups && whiteListedMachineryGroups.length > 0) {
          whiteListFilters = this.addFilterIfDefined(whiteListFilters, new ElasticSearchFilterValue(this.ID_FIELD, String(whiteListedMachineryGroups.join(','))));
          if (rootMachineryGroup) {
            whiteListFilters = this.addFilterIfDefined(whiteListFilters, new ElasticSearchFilterValue(this.ID_FIELD, String(rootMachineryGroup)));
          }
        }
        filters = this.addFilterIfDefined(filters, groupFilterValue);
        break;
      case ELASTIC_SEARCH_TYPE.GATEWAY:
      case ELASTIC_SEARCH_TYPE.USER:
        filters = this.addFilterIfDefined(filters, groupFilterValue);
        filters = this.addFilterIfDefined(filters, organizationFilterValue);
        break;
      case ELASTIC_SEARCH_TYPE.MACHINE:
        filters = this.addFilterIfDefined(filters, groupFilterValue);
        filters = this.addFilterIfDefined(filters, organizationFilterValue);
        if (whiteListedMachineryGroups && whiteListedMachineryGroups.length > 0) {
          whiteListFilters = this.addFilterIfDefined(whiteListFilters, new ElasticSearchFilterValue(ELASTIC_SEARCH_CONSTANTS.DIRECT_PARENT_MACHINERY_GROUP_PROPERTY_NAME, String(whiteListedMachineryGroups.join(','))));
        }
        break;
      default:
        break;
    }

    request.filter = filters;
    request.whiteListFilter = whiteListFilters;

  }

  public isInvalidSearchTerm(searchTerm): boolean {
    if (searchTerm === '') {
      return false;
    }

    const regexPattern = new RegExp('^[' + ELASTIC_SEARCH_CONSTANTS.SPECIAL_CHARACTERS_PATTERN.join('') + ']+$');
    return regexPattern.test(searchTerm);
  }
}
